import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useQueryStates, parseAsInteger, parseAsString, parseAsIsoDateTime } from 'nuqs';
import { FilterGroup, FilterOperator, SortOrder, Comparator } from '@/lib/utils';
import { SearchAlertsAdminQuery, SearchAlertsQuery } from '@/lib/api/monitoring/alert';
import { SortingState, Updater, VisibilityState } from '@tanstack/react-table';
import { EnumAlertStatus } from '@/lib/api/types.gen';
import { alertDataTableColumns, extractAlertTableData } from '../table';
import {
  DataTable,
  DataTableContainer,
  DataTableContent,
  DataTableFilterBar,
  DataTableHeader,
  DataTableRows,
  DataTableWrapper,
  DataTableFooter,
  DataTableUtilityBar,
} from '@/components/data-table';
import {
  FilterForm,
  FilterField,
  FilterHeader,
  FilterInlineResetButton,
} from '@/components/filters/FilterForm';
import { ScrollArea } from '@/components/ui/scroll-area';

// URL param parsers for alert filters
const alertFilterParsers = {
  page: parseAsInteger.withDefault(1),
  pageSize: parseAsInteger.withDefault(24),
  alertId: parseAsString,
  projectName: parseAsString,
  companyName: parseAsString,
  status: parseAsString,
  actioned: parseAsString,
  isClosed: parseAsString,
  hadSlaBreach: parseAsString,
  occurredFrom: parseAsIsoDateTime,
  occurredTo: parseAsIsoDateTime,
  closedAtFrom: parseAsIsoDateTime,
  closedAtTo: parseAsIsoDateTime,
};

interface AlertsTableProps {
  /** Use admin query */
  admin?: boolean;
  /** Filter alerts by this site */
  siteId?: string;
  /** Hide top page header when embedding (e.g., site alerts tab) */
  showPageHeader?: boolean;
  /** Controlled column visibility */
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (updater: Updater<VisibilityState>) => void;
  /** Controlled sorting */
  sorting?: SortingState;
  onSortingChange?: (updater: Updater<SortingState>) => void;
  /** Show project filter input */
  showProjectFilter?: boolean;
  /** Show column visibility controls */
  showColumnToggle?: boolean;
  /** Controlled search value */
  alertIdSearch?: string;
  onAlertIdSearchChange?: (value: string) => void;
  /** Controlled filters visibility */
  showFilters?: boolean;
  onShowFiltersChange?: (show: boolean) => void;
  /** Render search row externally */
  renderSearchRowExternally?: boolean;
}

const pageSize = 24;

export function AlertsTable({
  admin,
  siteId,
  columnVisibility: columnVisibilityProp,
  onColumnVisibilityChange,
  sorting: sortingProp,
  onSortingChange,
  showProjectFilter = true,
  showColumnToggle = false,
}: AlertsTableProps) {
  // URL-synced filter state
  const [urlFilters, setUrlFilters] = useQueryStates(alertFilterParsers, {
    history: 'replace',
    clearOnDefault: true,
  });

  const [showFilters, setShowFilters] = useState(true);

  const [internalColumnVisibility, setInternalColumnVisibility] = useState<VisibilityState>({
    actionedAt: false,
    actionedBy: false,
    slaDue: false,
    duration: false,
  });
  const [internalSorting, setInternalSorting] = useState<SortingState>([
    { id: 'occurred', desc: true },
  ]);

  const columnVisibility = columnVisibilityProp ?? internalColumnVisibility;
  const setColumnVisibility = onColumnVisibilityChange ?? setInternalColumnVisibility;
  const sorting = sortingProp ?? internalSorting;
  const setSorting = onSortingChange ?? setInternalSorting;

  const sort = sorting[0] ?? { id: 'occurred', desc: true };

  // Build filter groups from URL params
  const filterGroups = useMemo(() => {
    const groups: FilterGroup[] = [];
    const filters: { field: string; value: string; action: Comparator }[] = [];

    // Text filters
    if (urlFilters.alertId) {
      filters.push({ field: 'alertId', value: urlFilters.alertId, action: Comparator.ILike });
    }
    if (urlFilters.projectName) {
      filters.push({
        field: 'projectName',
        value: urlFilters.projectName,
        action: Comparator.ILike,
      });
    }
    if (urlFilters.companyName) {
      filters.push({
        field: 'companyName',
        value: urlFilters.companyName,
        action: Comparator.ILike,
      });
    }

    // Select filters
    if (urlFilters.status) {
      filters.push({ field: 'status', value: urlFilters.status, action: Comparator.Eq });
    }
    if (urlFilters.actioned) {
      filters.push({ field: 'actioned', value: urlFilters.actioned, action: Comparator.Eq });
    }
    if (urlFilters.isClosed) {
      filters.push({ field: 'isClosed', value: urlFilters.isClosed, action: Comparator.Eq });
    }
    if (urlFilters.hadSlaBreach) {
      filters.push({
        field: 'hadSlaBreach',
        value: urlFilters.hadSlaBreach,
        action: Comparator.Eq,
      });
    }

    // Date filters
    if (urlFilters.occurredFrom) {
      filters.push({
        field: 'occurred',
        value: urlFilters.occurredFrom.toISOString(),
        action: Comparator.GtEq,
      });
    }
    if (urlFilters.occurredTo) {
      filters.push({
        field: 'occurred',
        value: urlFilters.occurredTo.toISOString(),
        action: Comparator.LtEq,
      });
    }
    if (urlFilters.closedAtFrom) {
      filters.push({
        field: 'closedAt',
        value: urlFilters.closedAtFrom.toISOString(),
        action: Comparator.GtEq,
      });
    }
    if (urlFilters.closedAtTo) {
      filters.push({
        field: 'closedAt',
        value: urlFilters.closedAtTo.toISOString(),
        action: Comparator.LtEq,
      });
    }

    if (filters.length > 0) {
      groups.push({ operator: FilterOperator.And, filters });
    }

    // Add siteId filter if provided via prop
    if (siteId) {
      groups.push({
        operator: FilterOperator.And,
        filters: [{ field: 'projectId', value: siteId, action: Comparator.Eq }],
      });
    }

    return groups;
  }, [urlFilters, siteId]);

  const alertsQuery = useQuery(
    admin
      ? SearchAlertsAdminQuery({
          pageNumber: urlFilters.page,
          pageSize: urlFilters.pageSize,
          sortBy: sort.id,
          sortDir: sort.desc ? SortOrder.Descending : SortOrder.Ascending,
          filterOperator: FilterOperator.And,
          filterFilterGroups: filterGroups,
        })
      : SearchAlertsQuery({
          pageNumber: urlFilters.page,
          pageSize: urlFilters.pageSize,
          projectId: siteId,
          sortBy: sort.id,
          sortDir: sort.desc ? SortOrder.Descending : SortOrder.Ascending,
          filterOperator: FilterOperator.And,
          filterFilterGroups: filterGroups,
        })
  );

  const totalAlertCount = alertsQuery.data?.totalRows;
  const alertData = useMemo(() => {
    const alerts = alertsQuery.data?.rows ?? [];
    return extractAlertTableData(alerts);
  }, [alertsQuery.data?.rows]);

  const [filterCount, setFilterCount] = useState(
    Object.values(urlFilters).filter((v) => v !== undefined && v !== null && v !== '').length
  );
  const filtersOpen = showFilters;
  const tableTopRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (tableTopRef.current) {
      tableTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [urlFilters.page, urlFilters.pageSize]);

  const handleFiltersChange = ({
    values,
  }: {
    filterOperator: FilterOperator;
    filterFilterGroups: FilterGroup[];
    values: Record<string, string | Date | undefined>;
  }) => {
    const updates: Partial<typeof urlFilters> = {};
    Object.entries(values).forEach(([key, value]) => {
      if (key in alertFilterParsers) {
        (updates as Record<string, unknown>)[key] = value ?? null;
      }
    });
    setUrlFilters({ ...updates, page: 1 });
    setFilterCount(
      Object.values(values).filter((v) => v !== undefined && v !== null && v !== '').length
    );
  };

  return (
    <div ref={tableTopRef} className="relative">
      <DataTable
        columns={alertDataTableColumns}
        data={alertData}
        loading={alertsQuery.isLoading && !alertsQuery.data}
        error={alertsQuery.error as Error | null}
        page={urlFilters.page}
        pageSize={urlFilters.pageSize || pageSize}
        totalCount={totalAlertCount}
        onPageChange={(p: number) => setUrlFilters({ page: p })}
        pageSizeOptions={[24, 50, 100]}
        stateKey="alerts-table"
        emptyMessage="No alerts found"
        sidebarOpen={filtersOpen}
        onSidebarOpenChange={(open) => setShowFilters(open)}
        defaultSorting={sorting}
        onSortingChange={setSorting}
        defaultColumnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        onPageSizeChange={(newSize: number) => setUrlFilters({ page: 1, pageSize: newSize })}
      >
        <FilterForm
          defaultValues={{
            projectName: urlFilters.projectName ?? undefined,
            companyName: urlFilters.companyName ?? undefined,
            status: urlFilters.status ?? undefined,
            actioned: urlFilters.actioned ?? undefined,
            isClosed: urlFilters.isClosed ?? undefined,
            hadSlaBreach: urlFilters.hadSlaBreach ?? undefined,
            occurredFrom: urlFilters.occurredFrom ?? undefined,
            occurredTo: urlFilters.occurredTo ?? undefined,
            closedAtFrom: urlFilters.closedAtFrom ?? undefined,
            closedAtTo: urlFilters.closedAtTo ?? undefined,
          }}
          onFiltersChange={handleFiltersChange}
        >
            <DataTableContainer>
              <DataTableFilterBar>
              <div className="flex min-h-0 flex-col" data-onboarding="admin-alerts-filters-panel">
                <FilterHeader 
                  onClose={() => setShowFilters(false)} 
                />
                <ScrollArea className="flex-1 px-4 pb-6" style={{ height: 'calc(100vh - 128px)' }}>
                  <div className="space-y-4 pb-4 pt-2">
                    <FilterInlineResetButton
                      onReset={() => {
                        setUrlFilters({
                          projectName: null,
                          companyName: null,
                          status: null,
                          actioned: null,
                          isClosed: null,
                          hadSlaBreach: null,
                          occurredFrom: null,
                          occurredTo: null,
                          closedAtFrom: null,
                          closedAtTo: null,
                          page: 1,
                        });
                        setFilterCount(0);
                      }}
                    />
                  </div>
                  <div className="space-y-6 pb-4">
                    <div className="flex flex-col gap-4">
                      <div className="grid grid-cols-1 gap-4">
                        {showProjectFilter && (
                          <FilterField
                            type="text"
                            name="projectName"
                            label="Project Name"
                            field="projectName"
                            comparator={Comparator.ILike}
                            placeholder="Project name..."
                          />
                        )}
                        <FilterField
                          type="text"
                          name="companyName"
                          label="Company Name"
                          field="companyName"
                          comparator={Comparator.ILike}
                          placeholder="Company name..."
                        />
                      </div>
                      <FilterField
                        type="toggle"
                        name="status"
                        label="Status"
                        field="status"
                        comparator={Comparator.Eq}
                        options={Object.values(EnumAlertStatus).map((s) => ({ label: s, value: s }))}
                      />
                      <FilterField
                        type="toggle"
                        name="actioned"
                        label="Actioned"
                        field="actioned"
                        comparator={Comparator.Eq}
                        options={[
                          { label: 'Yes', value: 'true' },
                          { label: 'No', value: 'false' },
                        ]}
                      />
                      <FilterField
                        type="toggle"
                        name="isClosed"
                        label="Closed"
                        field="isClosed"
                        comparator={Comparator.Eq}
                        options={[
                          { label: 'Yes', value: 'true' },
                          { label: 'No', value: 'false' },
                        ]}
                      />
                      <FilterField
                        type="toggle"
                        name="hadSlaBreach"
                        label="SLA Breached"
                        field="hadSlaBreach"
                        comparator={Comparator.Eq}
                        options={[
                          { label: 'Yes', value: 'true' },
                          { label: 'No', value: 'false' },
                        ]}
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="text-sm font-semibold text-white/80">Occurred</div>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <FilterField
                          type="date"
                          name="occurredFrom"
                          label=""
                          field="occurred"
                          comparator={Comparator.GtEq}
                          placeholder="From date"
                        />
                        <FilterField
                          type="date"
                          name="occurredTo"
                          label=""
                          field="occurred"
                          comparator={Comparator.LtEq}
                          placeholder="To date"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="text-sm font-semibold text-white/80">Closed</div>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <FilterField
                          type="date"
                          name="closedAtFrom"
                          label=""
                          field="closedAt"
                          comparator={Comparator.GtEq}
                          placeholder="From date"
                        />
                        <FilterField
                          type="date"
                          name="closedAtTo"
                          label=""
                          field="closedAt"
                          comparator={Comparator.LtEq}
                          placeholder="To date"
                        />
                      </div>
                    </div>
                  </div>
                </ScrollArea>
              </div>
            </DataTableFilterBar>
            <DataTableContent className="w-full flex-1 min-w-0">
              <div data-onboarding="admin-alerts-column-toggle">
                <DataTableUtilityBar
                  showFilterToggle
                  showColumnVisibility={showColumnToggle}
                  search={{
                    value: urlFilters.alertId ?? '',
                    onChange: (value) => setUrlFilters({ alertId: value || null, page: 1 }),
                    placeholder: 'Search by Alert ID...',
                  }}
                  filterCount={filterCount}
                />
              </div>
              <DataTableWrapper>
                <DataTableHeader />
                <DataTableRows />
              </DataTableWrapper>
              <DataTableFooter />
            </DataTableContent>
          </DataTableContainer>
        </FilterForm>
      </DataTable>
    </div>
  );
}
